import type {FetchFn} from 'src/internal/transport/types';

type FetchOptions = Parameters<FetchFn>[1];

const supportsKeepalive = (): boolean =>
  typeof Request !== 'undefined' && 'keepalive' in Request.prototype;

const canSendBeacon = (): boolean =>
  typeof navigator !== 'undefined' &&
  typeof navigator.sendBeacon === 'function';

const fetchWithoutKeepalive = (
  url: string,
  options: FetchOptions,
): Promise<unknown> => fetch(url, {...options, keepalive: false});

const sendBeaconOrFetch = (
  url: string,
  options: FetchOptions,
): Promise<unknown> => {
  const payload = new Blob([String(options.body)], {
    type: options.headers['Content-Type'],
  });

  try {
    if (navigator.sendBeacon(url, payload)) {
      return Promise.resolve();
    }
  } catch {
    return fetchWithoutKeepalive(url, options);
  }

  return fetchWithoutKeepalive(url, options);
};

export default function createBrowserFetch(): FetchFn {
  return (url, options) => {
    if (!options.keepalive) {
      return fetch(url, options);
    }

    if (supportsKeepalive()) {
      return fetch(url, options).catch(() =>
        fetchWithoutKeepalive(url, options),
      );
    }

    if (canSendBeacon()) {
      return sendBeaconOrFetch(url, options);
    }

    return fetchWithoutKeepalive(url, options);
  };
}
